import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { LoginForm } from '@/components/LoginForm';
import { Logo } from '@/components/Logo';
import { useTheme } from '@/components/ThemeProvider';
import { FloatingCubes } from '@/components/FloatingCubes';

import { useQuery } from 'hooks/query';

import { ChainlitContext, useAuth } from '@chainlit/react-client';

export const LoginError = new Error('Error logging in. Please try again later.');

export default function Login() {
  const query = useQuery();
  const { data: config, user, setUserFromAPI } = useAuth();
  const [error, setError] = useState('');
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  const { variant } = useTheme();
  const isDarkMode = variant === 'dark';

  const handleCookieAuth = (json: any): void => {
    if (json?.success != true) throw LoginError;

    // Validate login cookie and get user data.
    setUserFromAPI();
  };

  const handleAuth = async (jsonPromise: Promise<any>, redirectURL?: string) => {
    try {
      const json = await jsonPromise;

      handleCookieAuth(json);

      if (redirectURL) {
        navigate(redirectURL);
      }
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleHeaderAuth = async () => {
    const jsonPromise = apiClient.headerAuth();

    // Why does apiClient redirect to '/' but handlePasswordLogin to callbackUrl?
    await handleAuth(jsonPromise, '/');
  };

  const handlePasswordLogin = async (email: string, password: string) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    const jsonPromise = apiClient.passwordAuth(formData);
    await handleAuth(jsonPromise);
  };

  useEffect(() => {
    setError(query.get('error') || '');
  }, [query]);

  useEffect(() => {
    if (!config) {
      return;
    }
    if (!config.requireLogin) {
      navigate('/');
    }
    if (config.headerAuth && !user) {
      handleHeaderAuth();
    }
    if (user) {
      navigate('/');
    }
  }, [config, user]);

  // TODO: Replace with the actual hero image URL
  const heroImageUrl = isDarkMode
    ? 'https://www.paceengineering.us/wp-content/uploads/2023/04/ElectricalComm-Low-Voltage-SystemFORTUNAHUMBOLDTCALIFORNIA-scaled.jpg'
    : 'https://www.paceengineering.us/wp-content/uploads/2023/05/News-scaled.jpg';

  return (
    <div className="grid relative min-h-svh">
      <div className="absolute inset-0 z-0">
        {heroImageUrl && (
          <>
            <img src={heroImageUrl} alt="" className="object-cover object-center w-full h-full" />
            <div
              className={`absolute inset-0 ${isDarkMode ? 'bg-black/50' : 'bg-black/50'}`}
              aria-hidden
            />
          </>
        )}
      </div>

      <div className="flex overflow-hidden relative z-10 flex-col gap-4 p-6 md:p-10">
        <FloatingCubes />
        <div className="flex relative z-10 flex-col flex-1 gap-4">
          <div className="flex gap-2 justify-center md:justify-start">
            <img
              src={isDarkMode ? '/images/logo.png' : '/images/logo.png'}
              alt="Logo"
              className="w-[150px] filter drop-shadow-[0_0_4px_rgba(255,255,255,0.9)]"
            />
          </div>
          <div className="flex flex-1 justify-center items-center">
            <div className="w-full max-w-xs">
              <LoginForm
                error={error}
                callbackUrl="/"
                providers={config?.oauthProviders || []}
                onPasswordSignIn={config?.passwordAuth ? handlePasswordLogin : undefined}
                onOAuthSignIn={async (provider: string) => {
                  window.location.href = apiClient.getOAuthEndpoint(provider);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
